// "Export PDF" for the RRI USAJILI dashboard: the Dashboard tab (KPIs, the
// daily line chart, the per-county bar chart, the summary table) plus the
// daily figures behind the line chart, under the current filters, on
// landscape A4 pages.

import dayjs from 'dayjs';
import type { QueryClient } from '@tanstack/react-query';
import { usajiliApi } from '@services/api/usajili';
import { countyColor, formatCompact, formatNumber } from '@utils/usajiliFormat';
import { KENYA_COUNTY_PATHS } from '@components/kenyaCountyPaths';
import { exportPdf } from '@utils/pdfExport';
import type { USAJILIFilterState, USAJILIMetadata } from '@types';

export const PDF_TREND = '[data-pdf="usajili-trend"]';
export const PDF_TODATE = '[data-pdf="usajili-todate"]';

const mapKey = (c: string) => {
  const u = c.trim().toUpperCase();
  return u === 'ELGEYO MARAKWET' ? 'ELGEYO-MARAKWET' : u;
};
const SHADES = ['#C7E9C0', '#A1D99B', '#74C476', '#31A354', '#006D2C'];
const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;');

/** Kenya map drawn for the PDF (the dashboard has none on screen): counties
 *  with returns shaded by voters registered in five equal steps up to the
 *  highest, the rest grey. The RRI counties are small and close together, so
 *  they carry numbers with a key below; a single county is named instead. */
export function rriMapSvg(values: { county: string; value: number }[]) {
  const W = 480;
  const MAP_H = 508;
  const max = Math.max(1, ...values.map((v) => v.value));
  const shade = (v: number) => SHADES[Math.min(4, Math.floor((v / max) * 5))];
  const sorted = [...values].sort((x, y) => x.county.localeCompare(y.county));
  const byKey = new Map(sorted.map((v, i) => [mapKey(v.county), { ...v, no: i + 1 }]));
  const single = sorted.length === 1;
  const paths: string[] = [];
  const marks: string[] = [];
  for (const [name, d] of Object.entries(KENYA_COUNTY_PATHS)) {
    const v = byKey.get(name);
    paths.push(`<path d="${d}" fill="${v ? shade(v.value) : '#E5E7EB'}" stroke="#ffffff" stroke-width="0.8" stroke-linejoin="round"/>`);
    if (!v) continue;
    const n = (d.match(/-?\d+(?:\.\d+)?/g) || []).map(Number);
    const xs = n.filter((_, i) => i % 2 === 0);
    const ys = n.filter((_, i) => i % 2 === 1);
    const x = ((Math.min(...xs) + Math.max(...xs)) / 2).toFixed(1);
    const y = ((Math.min(...ys) + Math.max(...ys)) / 2).toFixed(1);
    marks.push(single
      ? `<text x="${x}" y="${y}" text-anchor="middle" font-size="15" font-weight="700" fill="#111827" stroke="#ffffff" stroke-width="3" paint-order="stroke">${esc(v.county)}</text>`
      : `<circle cx="${x}" cy="${y}" r="9" fill="#ffffff" stroke="#111827" stroke-width="1"/><text x="${x}" y="${(+y + 4).toFixed(1)}" text-anchor="middle" font-size="11" font-weight="700" fill="#111827">${v.no}</text>`);
  }
  let y = MAP_H + 18;
  const out: string[] = [`<text x="6" y="${y}" font-size="13" font-weight="700" fill="#374151">Voters registered (RRI returns)</text>`];
  y += 8;
  if (single) {
    out.push(`<g transform="translate(6,${y})"><rect width="14" height="14" rx="2" fill="${SHADES[4]}"/><text x="18" y="12" font-size="12" fill="#374151">${esc(sorted[0].county)}: ${formatNumber(sorted[0].value)}</text></g>`);
  } else SHADES.forEach((c, i) => {
    const lo = Math.round((max * i) / 5);
    const hi = Math.round((max * (i + 1)) / 5);
    out.push(`<g transform="translate(${6 + i * 94},${y})"><rect width="14" height="14" rx="2" fill="${c}"/><text x="18" y="12" font-size="12" fill="#374151">${formatCompact(lo)}–${formatCompact(hi)}</text></g>`);
  });
  y += 30;
  if (!single) {
    const half = Math.ceil(sorted.length / 2);
    sorted.forEach((v, i) => {
      const col = i < half ? 0 : 1;
      const row = i < half ? i : i - half;
      out.push(`<text x="${6 + col * 240}" y="${y + row * 19}" font-size="13" fill="#111827"><tspan font-weight="700">${i + 1}</tspan>  ${esc(v.county)}  <tspan fill="#4b5563">${formatNumber(v.value)}</tspan></text>`);
    });
    y += half * 19;
  }
  const H = y + 4;
  const markup = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" style="font-family: Helvetica, Arial, sans-serif">`
    + `<rect width="${W}" height="${H}" fill="#ffffff"/>${paths.join('')}${marks.join('')}${out.join('')}</svg>`;
  return { markup, width: W, height: H };
}

/** The red header line of the RRI dashboard, for the PDFs: RRI returns' date
 *  range, number of counties, and when the file was made. */
export function rriStatus(m: USAJILIMetadata | null | undefined) {
  return [
    'RRI returns',
    m?.data_from && m?.data_to ? `${dayjs(m.data_from).format('D MMM')} – ${dayjs(m.data_to).format('D MMM YYYY')}` : null,
    m?.counties?.length ? `${m.counties.length} counties` : null,
    dayjs().format('ddd D MMM YYYY, HH:mm'),
  ].filter(Boolean).join(' · ');
}

export async function exportUsajiliPdf(qc: QueryClient, filters: USAJILIFilterState, refetchKey: number, allCounties: string[], metadata?: USAJILIMetadata | null) {
  // Same query keys as the dashboard tabs, so this reuses what is on screen.
  const [summary, trend] = await Promise.all([
    qc.ensureQueryData({ queryKey: ['usajili_summary', filters, refetchKey], queryFn: () => usajiliApi.summary(filters) }),
    qc.ensureQueryData({ queryKey: ['usajili_trend_voters', filters, refetchKey], queryFn: () => usajiliApi.trendVoters(filters) }),
  ]);

  const nice = (d: string) => dayjs(d).format('D MMM YYYY');
  const period = !filters.date_from && !filters.date_to
    ? 'Total to date'
    : filters.date_from && filters.date_from === filters.date_to
      ? nice(filters.date_from)
      : `${filters.date_from ? nice(filters.date_from) : 'start'} – ${filters.date_to ? nice(filters.date_to) : 'latest'}`;
  const scope = [filters.county, filters.district, filters.division, filters.location].filter((v) => v && v !== 'All');
  const scopeLabel = scope.length ? scope.join(' / ') : 'All counties';

  const rows = summary.rows || [];
  const total = rows.find((r) => r.is_grand_total);
  const points = trend.points || [];
  const present = new Set(points.map((p) => p.county));
  const counties = allCounties.filter((c) => present.has(c));

  // Daily table: one row per date, a column per county, and the day's total.
  const byDate = new Map<string, Map<string, number>>();
  for (const p of points) {
    if (!byDate.has(p.date)) byDate.set(p.date, new Map());
    byDate.get(p.date)!.set(p.county, p.voters_registered);
  }
  const dates = Array.from(byDate.keys()).sort();
  const colTotals = counties.map(() => 0);
  const dailyRows = dates.map((d) => {
    const m = byDate.get(d)!;
    let dayTotal = 0;
    const cells = counties.map((c, i) => {
      const v = m.get(c);
      if (v === undefined) return '-';
      dayTotal += v;
      colTotals[i] += v;
      return formatNumber(v);
    });
    return [dayjs(d).format('D MMM YYYY'), ...cells, formatNumber(dayTotal)];
  });

  await exportPdf({
    title: scope.length ? `RRI USAJILI · ${scopeLabel}` : 'RRI USAJILI',
    status: rriStatus(metadata),
    subtitle: `${period} · ${scopeLabel} · rri.sdics.tech`,
    fileName: `rri-usajili-${scope.length ? `${scope.join('-').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-` : ''}${dayjs().format('YYYY-MM-DD')}`,
    blocks: [
      { kind: 'kpis', items: [
        { label: 'New IDs', value: formatNumber(total?.new_ids ?? 0), sub: `${period} · ${scopeLabel}`, color: '#1F77B4' },
        { label: 'ID Replacements', value: formatNumber(total?.id_replacements ?? 0), sub: `${period} · ${scopeLabel}`, color: '#FF7F0E' },
        { label: 'Voters Registered', value: formatNumber(total?.voters_registered ?? 0), sub: `${period} · ${scopeLabel}`, color: '#2CA02C' },
      ] },
      { kind: 'charts', height: 400, items: [
        {
          title: `${scopeLabel} · voters registered`,
          svg: rriMapSvg(rows.filter((r) => !r.is_grand_total).map((r) => ({ county: r.county, value: r.voters_registered }))),
          weight: 1.3,
        },
        {
          title: scope.length ? `Daily registration of voters · ${scopeLabel}` : 'Daily registration of voters per county',
          el: document.querySelector(PDF_TREND),
          legend: counties.map((c) => ({ label: c, color: countyColor(c) })),
          weight: 2,
        },
      ] },
      { kind: 'pageBreak' },
      { kind: 'charts', height: 300, items: [{
        title: 'New IDs, replacements and voters registered per county',
        el: document.querySelector(PDF_TODATE),
        legend: [
          { label: 'New IDs', color: '#1F77B4' },
          { label: 'ID Replacements', color: '#FF7F0E' },
          { label: 'Voters Registered', color: '#2CA02C' },
        ],
      }] },
      { kind: 'table', title: 'Summary table',
        columns: [{ label: 'County' }, { label: '# New IDs', align: 'right' }, { label: '# ID Replacements', align: 'right' }, { label: '# Voters Registered', align: 'right' }],
        rows: rows.filter((r) => !r.is_grand_total).map((r) => [r.county, formatNumber(r.new_ids), formatNumber(r.id_replacements), formatNumber(r.voters_registered)]),
        total: total ? ['Grand Total', formatNumber(total.new_ids), formatNumber(total.id_replacements), formatNumber(total.voters_registered)] : undefined },
      { kind: 'pageBreak' },
      { kind: 'table', title: 'Daily voters registered per county',
        columns: [{ label: 'Date' }, ...counties.map((c) => ({ label: c, align: 'right' as const })), { label: 'Total', align: 'right' }],
        rows: dailyRows,
        total: dates.length ? ['Total', ...colTotals.map(formatNumber), formatNumber(colTotals.reduce((a, b) => a + b, 0))] : undefined },
      { kind: 'note', text: 'Figures are the daily county returns uploaded to the RRI USAJILI dashboard, under the filters shown at the top of each page. "-" means no return for that county on that day.' },
    ],
  });
}
